import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Typography, Grid } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/styles';

import { useSnackbar } from 'notistack';

import * as actionTypes from './../../../../actions';

import * as campaignApi from './../../../../api/campaignApi';

import CandidateDocumentUploadModal from './../../../CandidateCampaignDetail/CandidateDetails/CandidateDocumentUploadModal';
import CandidateDocumentModal from './CandidateDocumentModal';
import { BlockHeader } from './../../../../components/LayoutComponents/BlockHeader';
import DraggableRequiredCandidateDocuments from './DraggableRequiredCandidateDocuments';
import RequiredDocumentListCard from './RequiredDocumentListCard';

import { NOT_SUBMITTED } from './../../../../constants/candidateStatusIds';
import { ADMIN, CONSULTANT, COREADMIN, SYSTEMADMIN, CONTACT, CANDIDATE } from './../../../../constants/roles';

const useStyles = makeStyles((theme) => ({
  gridContainer: {
    alignItems: 'center'
  },
  action: {
    alignSelf: 'center'
  },
  documentList: {
    marginBottom: theme.spacing(4)
  },
  addDocumentButton: {
    minWidth: 25,
    padding: 2,
    float: 'right'
  },
  progressCircle: {
    color: theme.palette.primary.contrastText
  },
  topPadding: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  documentsOrderButton: {
    marginTop: 20
  }
}));

const RequiredCandidateDocuments = ({ candidateView, campaignRequiredDocuments, ...rest }) => {

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [t] = useTranslation(['campaigns', 'common', 'snackbar', 'tooltips']);
  const classes = useStyles();

  const { consultancyId } = useSelector((state) => state.session);
  const role = useSelector((state) => state.session.roleType);
  const roleType = useSelector((state) => state.session.roleType);
  const { id: profileId, candidateStatus } = useSelector((state) => state?.candidateCampaign?.candidateProfile || {});
  const adminContactPreviewMode = useSelector((state) => state?.campaign?.adminContactPreview);
  const campaignId = useSelector((state) => {
    if (state.campaign?.id) {
      return state.campaign.id;
    } else if (state.talentAlert.id) {
      return state.talentAlert.id;
    } else if (state.candidateCampaign?.campaignId) {
      return;
    }

    return state.candidateCampaign.candidateProfile.campaignId;
  });
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [editDocument, setEditDocument] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(candidateStatus === NOT_SUBMITTED);
  const [requiredDocuments, setRequiredDocuments] = useState();
  const [textModal, setTextModal] = useState(null);

  const documentsWithUrl = requiredDocuments?.filter((document) => {
    if (role === CONTACT || adminContactPreviewMode) {
      return document.url && document?.visibility?.includes('contact'); //this filter required for admin in client preview mode to show only client visible docs
    }

    return document;
  });

  useEffect(() => {
    if (campaignRequiredDocuments) {
      setRequiredDocuments([...campaignRequiredDocuments]);
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsSubmitted(candidateStatus !== NOT_SUBMITTED);
  }, [candidateStatus]);

  useEffect(() => {
    setRequiredDocuments([...campaignRequiredDocuments]);
  }, [campaignRequiredDocuments]);

  const onOpenModal = () => {
    setAddDocumentModalOpen(true);
  };

  const onCloseModal = () => {
    setAddDocumentModalOpen(false);
    setTextModal(null);
    setEditDocument({});
  };

  const handleRemove = (id) => {
    campaignApi.removeRequiredCandidateDocument(consultancyId, campaignId, id)
      .then(() => {
        const index = requiredDocuments.indexOf(requiredDocuments.find((document) => document.id === id));

        if (index > -1) {
          dispatch({
            type: actionTypes.REMOVE_SINGLE_CAMPAIGN_REQUIRED_DOCUMENT,
            id,
          });

          setRequiredDocuments((prevDocuments) => [
            ...prevDocuments.filter((item, i) => i !== index),
          ]);

          enqueueSnackbar(t('common:documentDeleted'), { variant: 'success' });

          setEditDocument(false);
          onCloseModal();
        }
      })
      .catch(() => {
        enqueueSnackbar(t('common:documentDeletedError'), { variant: 'error' });
      });
  };

  const addDocument = (document) => {
    if (!document.update) {
      setRequiredDocuments((prevDocuments) => [...prevDocuments, document]);
      return;
    }

    const requiredDocument = requiredDocuments.find((doc) => doc.id === document.id);
    const index = requiredDocuments.indexOf(requiredDocument);

    if (document.candidateDocument) {
      delete document.update;
      delete document.candidateDocument;

      if (document.text) {
        requiredDocuments[index].text = document.text;
        requiredDocuments[index].document = {};
        requiredDocuments[index].url = '';
      } else {
        requiredDocuments[index].document = document.document;
        requiredDocuments[index].url = document.url;
        requiredDocuments[index].text = '';
      }
    } else {
      delete document.update;
      requiredDocuments[index] = document;
    }

    setRequiredDocuments([...requiredDocuments]);
  };

  const onDragEnd = (result) => {
    // return if item was dropped outside
    if (!result.destination) return;

    // return if the item was dropped in the same place
    if (result.source.droppableId === result.destination.droppableId
        && result.source.index === result.destination.index) {
      return;
    }

    // get the items array
    const newItems = requiredDocuments;

    // get the draggedItems
    const draggedItem = newItems[result.source.index];

    // delete the item from source position and insert it to the destination positon
    newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, draggedItem);

    //assign priority to all items
    newItems.forEach((item, index) => (item.priority = index + 1));

    // update state
    setRequiredDocuments(newItems);
  };

  const saveDocumentsOrder = () => {
    campaignApi.saveCandidateDocumentsPriorityOrder(consultancyId, campaignId, requiredDocuments)
      .then((response) => {
        if (response.status === 200) {
          dispatch({
            type: actionTypes.UPDATE_CAMPAIGN_REQUIRED_DOCUMENTS_PRIORITY,
            documents: requiredDocuments,
          });

          enqueueSnackbar(t('snackbar:succesfullySavedDocumentsOrder'), { variant: 'success' });
        }
      })
      .catch(() => {
        enqueueSnackbar(t('snackbar:unableToSaveDocumentsOrder'), { variant: 'error' });
      });
  };

  const getHeaderTooltipValue = () => {
    if (!candidateView && !rest?.guestView && !adminContactPreviewMode && !window.location.href.includes("candidates")) {
      return t('tooltips:addDeleteReorderCandidateDocumentsBlock');
    }

    if (candidateView && roleType !== CANDIDATE && roleType !== CONTACT) {
      return t('tooltips:reviewCandidateDocs');
    }

    return null;
  };

  const getComment = () => {
    // If role is contact or admin in client preview mode and there is not one doc with url then show comment : no docs
    if ((role === CONTACT || adminContactPreviewMode) && !documentsWithUrl?.length) {
      return (
        <Typography variant="h5">
          { t("campaigns:noDocumentsProvided") }
        </Typography>
      );
    }

    // If role is contact or admin in client preview mode and docs have urls then do not show any comments
    // Show no comment if admin roles and section is Campaign Required Documents in Campaign view
    if (((role === CONTACT || adminContactPreviewMode) && documentsWithUrl?.length) ||
      (role === ADMIN || role === CONSULTANT || role === COREADMIN || role === SYSTEMADMIN)) {
      return null;
    }

    return (
      <Grid item xs={12}>
        <Typography className={classes.topPadding} variant="h5">
          { t("campaigns:candidateRequiredDocsInstructions") }
        </Typography>
      </Grid>
    );
  };
  
  const filterRequiredDocuments = (document) => {
    if (role === CONTACT || adminContactPreviewMode) {
      // This filter is required for admin in client preview mode to show only client visible docs
      return document.url && document?.visibility?.includes("contact");
    }
    
    return document;
  };

  const mapRequiredDocuments = (document, index) => {
    if (role === CONTACT && !document.url) {
      return;
    }

    return (
      <RequiredDocumentListCard
        key={ index.toString() }
        document={ document }
        onRemove={ handleRemove }
        setMethods={ { setEditDocument, setAddDocumentModalOpen, setTextModal } }
        consultancyId={ consultancyId }
        candidateView={ candidateView }
        isSubmitted={ isSubmitted }
        guestView={ rest?.guestView } />
    );
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={0} className={classes.gridContainer}>
        <BlockHeader
          card
          header={ role === CONTACT || adminContactPreviewMode
            ? t("campaigns:candidateDocumentsLabel")
            : t("campaigns:requiredCandidateDocumentsLabel") }
          tooltip={ t("common:addDocument") }
          headerTooltip={ getHeaderTooltipValue() }
          icon={
            (!candidateView && !rest?.guestView && !adminContactPreviewMode) && (
              <AddIcon />
            )
          }
          btnFunc={ onOpenModal } />

        { getComment() }
      </Grid>

      {
        !candidateView && !window.location.href.includes("candidates") && role !== CONTACT ? (
          <DraggableRequiredCandidateDocuments
            onDragEnd={onDragEnd}
            requiredDocuments={requiredDocuments}
            handleRemove={handleRemove}
            setEditDocument={setEditDocument}
            setAddDocumentModalOpen={setAddDocumentModalOpen}
            setTextModal={setTextModal}
            consultancyId={consultancyId}
            candidateView={candidateView}
            isSubmitted={isSubmitted}
            saveDocumentsOrder={saveDocumentsOrder}
            guestView={rest?.guestView} />
        ) : (
          <div className={classes.documentList}>
            {
              requiredDocuments?.length > 0 && (
                requiredDocuments
                  .filter(filterRequiredDocuments)
                  .map(mapRequiredDocuments)
              )
            }
          </div>
        )
      }

      {
        candidateView ? (
          <CandidateDocumentUploadModal
            profileId={profileId}
            open={addDocumentModalOpen}
            onClose={onCloseModal}
            document={editDocument}
            campaignId={campaignId}
            onSubmit={addDocument}
            candidateView={candidateView}
            isSubmitted={isSubmitted} />
        ) : (
          <CandidateDocumentModal
            open={addDocumentModalOpen}
            onClose={onCloseModal}
            document={editDocument}
            campaignId={campaignId}
            onSubmit={addDocument}
            consultancyId={parseInt(consultancyId)}
            candidateView={candidateView}
            text={textModal}
            onRemove={handleRemove} />
        )
      }
    </div>
  );

};

export default RequiredCandidateDocuments;
